import { SectorFormData } from '@/features/properties/types';
import { FormField, FormInput } from '@practium/ui';
import styles from '../PropertyForm.module.css';


type IrrigationType =
    | 'none'
    | 'drip'
    | 'sprinkler'
    | 'microSprinkler'
    | 'pivot';

interface IrrigationFieldsProps {
    irrigationType: IrrigationType;
    irrigation: SectorFormData['irrigation'];
    setIrrigation: (
        key: keyof SectorFormData['irrigation'],
        value: string | number
    ) => void;
}

interface IrrigationViewFieldsProps {
    irrigationType: IrrigationType;
    irrigation: SectorFormData['irrigation'];
}

function formatValue(value: number | string | undefined, suffix: string) {
    if (value === undefined || value === null || value === '') return '—';
    const formatted = typeof value === 'number' ? value.toLocaleString('pt-BR') : value;
    return `${formatted} ${suffix}`;
}

function ViewField({ label, value }: { label: string; value: string }) {
    return (
        <div className={styles.viewField}>
            <span className={styles.viewLabel}>{label}</span>
            <span className={styles.viewValue}>{value}</span>
        </div>
    );
}

export function IrrigationFields({
    irrigationType,
    irrigation,
    setIrrigation,
}: IrrigationFieldsProps) {
    const values = irrigation ?? { irrigationEfficiency: '' };

    switch (irrigationType) {
        case 'pivot':
            return (
                <>
                    <FormField label="Eficiência da Irrigação">
                        <FormInput
                            type="number"
                            value={values.irrigationEfficiency}
                            onChange={(e) =>
                                setIrrigation('irrigationEfficiency', e.target.value)
                            }
                            suffix="%"
                            placeholder="Ex.: 85"
                        />
                    </FormField>

                    <FormField label="Lâmina Bruta de Irrigação">
                        <FormInput
                            type="number"
                            value={values.grossIrrigationDepth ?? ''}
                            onChange={(e) =>
                                setIrrigation('grossIrrigationDepth', e.target.value)
                            }
                            placeholder="Ex.: 8"
                            suffix="mm"
                        />
                    </FormField>

                    <FormField label="Tempo de Operação">
                        <FormInput
                            type="number"
                            value={values.operatingTime ?? ''}
                            onChange={(e) =>
                                setIrrigation('operatingTime', e.target.value)
                            }
                            placeholder="Ex.: 12"
                            suffix="h"
                        />
                    </FormField>
                </>
            );

        case 'drip':
            return (
                <>
                    <FormField label="Eficiência da Irrigação">
                        <FormInput
                            type="number"
                            value={values.irrigationEfficiency}
                            onChange={(e) =>
                                setIrrigation('irrigationEfficiency', e.target.value)
                            }
                            suffix="%"
                            placeholder="Ex.: 90"
                        />
                    </FormField>

                    <FormField label="Espaçamento entre Gotejadores">
                        <FormInput
                            type="number"
                            value={values.dripperSpacing ?? ''}
                            onChange={(e) =>
                                setIrrigation('dripperSpacing', e.target.value)
                            }
                            suffix="m"
                            placeholder="Ex.: 0.5"
                        />
                    </FormField>

                    <FormField label="Vazão do Gotejador">
                        <FormInput
                            type="number"
                            value={values.dripperFlow ?? ''}
                            onChange={(e) =>
                                setIrrigation('dripperFlow', e.target.value)
                            }
                            suffix="L/h"
                            placeholder="Ex.: 2"
                        />
                    </FormField>
                </>
            );

        case 'sprinkler':
            return (
                <>
                    <FormField label="Eficiência da Irrigação">
                        <FormInput
                            type="number"
                            value={values.irrigationEfficiency}
                            onChange={(e) =>
                                setIrrigation('irrigationEfficiency', e.target.value)
                            }
                            suffix="%"
                            placeholder="Ex.: 75"
                        />
                    </FormField>

                    <FormField label="Espaçamento entre Aspersores">
                        <FormInput
                            type="number"
                            value={values.sprinklerSpacing ?? ''}
                            onChange={(e) =>
                                setIrrigation('sprinklerSpacing', e.target.value)
                            }
                            suffix="m"
                            placeholder="Ex.: 12"
                        />
                    </FormField>

                    <FormField label="Vazão do Aspersor">
                        <FormInput
                            type="number"
                            value={values.sprinklerFlow ?? ''}
                            onChange={(e) =>
                                setIrrigation('sprinklerFlow', e.target.value)
                            }
                            suffix="L/h"
                            placeholder="Ex.: 800"
                        />
                    </FormField>
                </>
            );

        case 'microSprinkler':
            return (
                <>
                    <FormField label="Eficiência da Irrigação">
                        <FormInput
                            type="number"
                            value={values.irrigationEfficiency}
                            onChange={(e) =>
                                setIrrigation('irrigationEfficiency', e.target.value)
                            }
                            suffix="%"
                            placeholder="Ex.: 90"
                        />
                    </FormField>

                    <FormField label="Espaçamento entre Microaspersores">
                        <FormInput
                            type="number"
                            value={values.microSprinklerSpacing ?? ''}
                            onChange={(e) =>
                                setIrrigation('microSprinklerSpacing', e.target.value)
                            }
                            suffix="m"
                            placeholder="Ex.: 2"
                        />
                    </FormField>

                    <FormField label="Vazão do Microaspersor">
                        <FormInput
                            type="number"
                            value={values.microSprinklerFlow ?? ''}
                            onChange={(e) =>
                                setIrrigation('microSprinklerFlow', e.target.value)
                            }
                            suffix="L/h"
                            placeholder="Ex.: 60"
                        />
                    </FormField>
                </>
            );

        default:
            return null;
    }
}

export function IrrigationViewFields({ irrigationType, irrigation }: IrrigationViewFieldsProps) {
    const values = irrigation ?? { irrigationEfficiency: '' };

    switch (irrigationType) {
        case 'pivot':
            return (
                <>
                    <ViewField label="Eficiência da Irrigação" value={formatValue(values.irrigationEfficiency, '%')} />
                    <ViewField label="Lâmina Bruta de Irrigação" value={formatValue(values.grossIrrigationDepth, 'mm')} />
                    <ViewField label="Tempo de Operação" value={formatValue(values.operatingTime, 'h')} />
                </>
            );

        case 'drip':
            return (
                <>
                    <ViewField label="Eficiência da Irrigação" value={formatValue(values.irrigationEfficiency, '%')} />
                    <ViewField label="Espaçamento entre Gotejadores" value={formatValue(values.dripperSpacing, 'm')} />
                    <ViewField label="Vazão do Gotejador" value={formatValue(values.dripperFlow, 'L/h')} />
                </>
            );

        case 'sprinkler':
            return (
                <>
                    <ViewField label="Eficiência da Irrigação" value={formatValue(values.irrigationEfficiency, '%')} />
                    <ViewField label="Espaçamento entre Aspersores" value={formatValue(values.sprinklerSpacing, 'm')} />
                    <ViewField label="Vazão do Aspersor" value={formatValue(values.sprinklerFlow, 'L/h')} />
                </>
            );

        case 'microSprinkler':
            return (
                <>
                    <ViewField label="Eficiência da Irrigação" value={formatValue(values.irrigationEfficiency, '%')} />
                    <ViewField label="Espaçamento entre Microaspersores" value={formatValue(values.microSprinklerSpacing, 'm')} />
                    <ViewField label="Vazão do Microaspersor" value={formatValue(values.microSprinklerFlow, 'L/h')} />
                </>
            );

        default:
            return null;
    }
}
